import React, { useState } from 'react'

import { Chip } from '../chip'

import styles from './chipGroup.module.scss'

interface ChipGroupProps {
  chips: { label: string; value: string; closeable?: boolean; key?: string }[]
  direction?: 'horizontal' | 'vertical'
  onChipClick?: (label: string) => void
  onChipDismiss?: (label: string) => void
  clearAll?: boolean
  ariaLabel?: string
  chipGroupId?: string
  clearAllLabel?: string
  clearAllAriaLabel?: string
  className?: string
  chipClassName?: string
}

export const ChipGroup: React.FC<ChipGroupProps> = ({
  chips,
  direction = 'horizontal',
  onChipClick,
  onChipDismiss,
  clearAll = false,
  ariaLabel,
  chipGroupId,
  clearAllLabel = 'Clear All',
  clearAllAriaLabel = 'Clear all selected filters',
  className,
  chipClassName,
}) => {
  // Dismissable chips are active by default
  const [activeChips, setActiveChips] = useState<string[]>(chips.filter(chip => chip.closeable).map(chip => chip.value))

  const handleChipClick = (value: string) => {
    setActiveChips(prev => {
      if (prev.includes(value)) {
        return prev.filter(chip => chip !== value)
      } else {
        return [...prev, value]
      }
    })
    if (onChipClick) onChipClick(value)
  }

  const handleChipDismiss = (value: string) => {
    setActiveChips(prev => prev.filter(chip => chip !== value))
    if (onChipDismiss) onChipDismiss(value)
  }

  const handleClearAll = () => {
    setActiveChips([])
    if (onChipDismiss) {
      chips.forEach(chip => {
        onChipDismiss(chip.value)
      })
    }
  }

  return (
    <fieldset
      className={`${styles.chipGroup} ${styles[direction]} ${className}`}
      aria-label={ariaLabel ?? 'Filter chips'}
      id={chipGroupId}
    >
      {chips.map(chip => (
        <Chip
          key={chip.key ?? chip.label}
          label={chip.label}
          active={activeChips.includes(chip.label)}
          closeable={chip.closeable}
          onClick={() => {
            handleChipClick(chip.value)
          }}
          onClose={() => {
            handleChipDismiss(chip.value)
          }}
          aria-pressed={activeChips.includes(chip.label)}
          className={chipClassName}
        />
      ))}
      {clearAll && activeChips.length > 0 && (
        <button onClick={handleClearAll} className={styles.clearAllButton} aria-label={clearAllAriaLabel} type="button">
          {clearAllLabel} <span className={styles.clearAllIcon}>×</span>
        </button>
      )}
    </fieldset>
  )
}
